import {
  type Brand,
  type CalendarEventData,
  type CalendarEventSchedule,
  type Menu,
  MenuType,
  type RecordEntityId,
  type V2Menu,
} from '@centric-os/types';
import { cloneDeep, isEmpty, isNil } from 'lodash-es';
import { computed } from 'vue';
import { useCalendarStore, useStationStore } from '../../../../../../../stores';
import { storeToRefs } from 'pinia';
import { useMenuStore } from '@centric-os/stores/menus/use-menu-store';
import { useSiteStore } from '@centric-os/stores/sites/use-site-store';
import { DateTime } from 'luxon';
import type { MenuScheduleFormValues } from '../../../types';
import { RepeatValues, PriceLevels } from '../../../enums';
import { useCalendar } from '../../../../../../../../composables';

export default () => {
  // Set calendar store
  const calendarStore = useCalendarStore();
  const { currentEvent, isNewEvent } = storeToRefs(calendarStore);

  // Set menu store
  const menuStore = useMenuStore();
  const { menus, menu, v2Menu } = storeToRefs(menuStore);

  // Set site store
  const siteStore = useSiteStore();
  const { siteLocalMenuGroups } = storeToRefs(siteStore);

  // Set station store
  const stationStore = useStationStore();
  const { stationBrand } = storeToRefs(stationStore);

  // Set calendar composable
  const { getRepeatValueFromSchedule } = useCalendar();

  // Check if brand selection should be displayed
  const shouldDisplayBrandSelection = computed<boolean>(() => {
    // If its update schedule event, validate based on menu type
    if (!isNewEvent.value) {
      return menuType.value === MenuType.CENTRIC_OS;
    }
    // If its new schedule event, validate based on site local menu groups
    return !isEmpty(siteLocalMenuGroups.value);
  });

  // Get menu type
  const menuType = computed<MenuType>(() => {
    const fetchedMenu = menus.value.find((menu) => menu.id === currentEvent.value?.data?.menu);

    if (fetchedMenu === undefined) {
      if (currentEvent.value?.data?.version >= 3) {
        return MenuType.CENTRIC_OS;
      }
      return MenuType.V2;
    }
    return fetchedMenu?.meta?.menu_type;
  });

  const requirePickupOrDelivery = !!(
    stationBrand.value?.is?.pickup_supported || stationBrand.value?.is?.delivery_supported
  );

  // Get price level id from child menu
  const getPriceLevelId = (data: CalendarEventData): RecordEntityId => {
    if (data?.menu) {
      const childMenuStore = useMenuStore(`child-menu`);
      const { menu: childMenu } = storeToRefs(childMenuStore);

      return childMenu.value?.price_level_id || PriceLevels.DEFAULT_PRICE_LEVEL;
    }
    return PriceLevels.DEFAULT_PRICE_LEVEL;
  };

  const createDefaultInitialValues = (today: DateTime): MenuScheduleFormValues => ({
    requirePickupOrDelivery,
    eventMenu: undefined,
    startTime: today.toJSDate(),
    endTime: today.toJSDate(),
    repeat: { week: 1 },
    byWeekday: [],
    startDate: today.toJSDate(),
    endDate: today.toJSDate(),
    pickup: false,
    delivery: false,
    hidden: false,
    noEndDate: false,
    repeatInterval: RepeatValues.EVERYDAY,
    priceLevel: PriceLevels.DEFAULT_PRICE_LEVEL,
  });

  const createInitialValuesFromEvent = (
    data: CalendarEventData,
    schedule: CalendarEventSchedule,
    menuType: MenuType,
    menu: Menu,
    v2Menu: V2Menu,
  ): MenuScheduleFormValues => {
    const noEndDate = !schedule?.end;

    const startTimeDate =
      (schedule.times?.[0] && DateTime.fromFormat(schedule.times[0], 'HH:mm')) ||
      DateTime.fromFormat('00:00', 'HH:mm');

    const dayInMinutes = 60 * 24;
    const durationInMinutes = schedule?.durationUnit ? schedule.duration : dayInMinutes;

    const startTime = startTimeDate.toJSDate();
    const endTime = startTimeDate.plus({ minutes: durationInMinutes }).toJSDate();

    const repeat = schedule?.every || { week: 1 };
    const byWeekday = schedule?.dayOfWeek || [];
    const startDate = schedule.start ? DateTime.fromMillis(schedule.start).toJSDate() : undefined;
    const endDate = schedule.end ? DateTime.fromMillis(schedule.end).toJSDate() : undefined;
    const pickup = data?.is?.pickup || false;
    const delivery = data?.is?.delivery || false;
    const hidden = data?.is?.disabled || false;
    const priceLevel = getPriceLevelId(data);

    if (menuType === MenuType.CENTRIC_OS && menu) {
      const eventMenu = { ...menu, meta: { menu_type: menuType } };
      const brand = cloneDeep(eventMenu.brand);
      if (brand) {
        Object.keys(brand).forEach(
          (key) => brand[key as keyof Brand] === null && delete brand[key as keyof Brand],
        );
      }

      delete eventMenu.brand;

      return {
        requirePickupOrDelivery,
        eventMenu,
        startTime,
        endTime,
        repeat,
        byWeekday,
        startDate,
        endDate,
        pickup,
        delivery,
        hidden,
        noEndDate,
        repeatInterval: getRepeatValueFromSchedule(schedule),
        brand,
        priceLevel,
      };
    }

    const eventMenu = v2Menu
      ? { ...v2Menu, meta: { menu_type: menuType }, name: v2Menu.label?.en }
      : undefined;

    if (eventMenu) {
      delete eventMenu.label;
    }

    return {
      requirePickupOrDelivery,
      eventMenu,
      startTime,
      endTime,
      repeat,
      byWeekday,
      startDate,
      endDate,
      pickup,
      delivery,
      hidden,
      noEndDate,
      repeatInterval: getRepeatValueFromSchedule(schedule),
    };
  };

  // Computed for the initial values of the form
  const initialValues = computed<MenuScheduleFormValues>(() => {
    const today = DateTime.local().startOf('day');

    if (isNil(currentEvent.value) && isNil(menu.value)) {
      return createDefaultInitialValues(today);
    }

    const { data, schedule } = currentEvent.value;
    return createInitialValuesFromEvent(data, schedule, menuType.value, menu.value, v2Menu.value);
  });

  return { shouldDisplayBrandSelection, menuType, initialValues };
};
